import { CSSProperties, useEffect, useState } from 'react';
import { Box, Step, StepLabel, Stepper, Tooltip, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import IconButton from '@mui/material/IconButton';
import { Badge } from 'framework/ui/mui-builder';
import { EmployeeOfficeField } from 'domain/shared/employee';
import { useShowContext } from 'framework/show';
import useEmployeeContractsService from '../../../contracts/shared/employeeContractsService';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { Contract } from '../../../contracts/types';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import ContractDuractionField from 'domain/people/employee/shared/field/ContractDuractionField/ContractDuractionField';

interface CustomStepIconProps {
  active?: boolean;
  style?: CSSProperties;
  sx?: SxProps<Theme>;
}

const CustomStepIcon = ({ active, style }: CustomStepIconProps) => {
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor:
          style?.color || (active ? 'var(--mui-palette-success-light)' : 'var(--mui-palette-text-secondary)'),
        display: 'flex',
        zIndex: 2,
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
    />
  );
};

const ContractsTimeline = () => {
  const { data } = useShowContext();
  const { employeeAlias } = data.getData();
  const { addMessage } = useMessageService();
  const { getAll } = useEmployeeContractsService();
  const [contracts, setContracts] = useState<Contract[]>();
  const { getMomentDateObj, formatDate } = useDateService();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!contracts) {
      getAll(employeeAlias)
        .then(result => setContracts(result.result))
        .catch(() => {
          addMessage(new ErrorMessage('Failed to load contracts'));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MAX_VISIBLE_NODES = 10;

  if (!contracts || contracts.length === 0) {
    return null;
  }

  const sortedContracts = [...contracts].sort((a, b) => {
    const dateA = getMomentDateObj(a.startDate);
    const dateB = getMomentDateObj(b.startDate);
    return dateA.valueOf() - dateB.valueOf();
  });

  const shouldCollapse = sortedContracts.length > MAX_VISIBLE_NODES;

  const formatDateToMonthYear = (dateString: string) => {
    const date = getMomentDateObj(dateString);
    return formatDate(date, DATE_FORMAT.MONTH);
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const getVisibleContracts = () => {
    if (!shouldCollapse || expanded) {
      return sortedContracts;
    }

    const firstHalf = Math.floor(MAX_VISIBLE_NODES / 2);
    const secondHalf = MAX_VISIBLE_NODES - firstHalf - 1;

    return [
      ...sortedContracts.slice(0, firstHalf),
      { isExpandButton: true },
      ...sortedContracts.slice(sortedContracts.length - secondHalf),
    ];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Box>
        <ContractDuractionField
          сontracts={sortedContracts}
          sxBadge={{
            mb: 2,
            mr: 2,
          }}
        />
      </Box>
      <Stepper
        alternativeLabel
        sx={{
          overflowX: 'auto',
          height: '120px',
          fontSize: '12px',
          '& .expand-button .MuiStepLabel-root': {
            paddingTop: '10px',
          },
          '& .MuiStepConnector-root': {
            left: 'calc(-50%)',
            right: 'calc(50%)',
            marginTop: '11px',
          },
          '& .MuiStepLabel-root': { paddingTop: '20px' },
          '& .MuiStepLabel-label': { fontSize: '12px' },
        }}
      >
        {getVisibleContracts().map((contract, index, array) => {
          if ('isExpandButton' in contract) {
            return (
              <Step
                key="expand-button"
                active={false}
                sx={{ minWidth: '100px', width: '100px', maxWidth: '100px', p: 0 }}
                className="expand-button"
              >
                <StepLabel
                  slots={{
                    stepIcon: () => (
                      <IconButton
                        onClick={toggleExpanded}
                        style={{
                          padding: 0,
                          zIndex: 1,
                          backgroundColor: 'var(--mui-palette-background-paper)',
                        }}
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    ),
                  }}
                />
              </Step>
            );
          }

          const isLastStep = index === array.length - 1;

          const badgeColor = isLastStep ? 'success' : 'default';

          const startDateFormatted = formatDateToMonthYear(contract.startDate);
          const endDateFormatted = contract.isIndefinite ? '∞' : formatDateToMonthYear(contract.endDate);
          const dateRange = `${startDateFormatted} - ${endDateFormatted}`;

          return (
            <Step
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              active={isLastStep}
              sx={{
                minWidth: '100px',
                width: '100px',
                maxWidth: '100px',
                p: 0,
                '.Mui-disabled': { cursor: 'default' },
              }}
            >
              <Tooltip
                title={
                  <>
                    <Typography variant="caption">Start date: {contract.startDate}</Typography>
                    <Typography variant="caption">
                      End date: {contract.isIndefinite ? '∞' : contract.endDate}
                    </Typography>
                    {contract.employeePositionName && (
                      <Typography variant="caption"> Position: {contract.employeePositionName}</Typography>
                    )}
                  </>
                }
                arrow
                placement="top"
              >
                <StepLabel
                  slots={{
                    stepIcon: props => (
                      <CustomStepIcon
                        {...props}
                        sx={{
                          paddingTop: '20px',
                        }}
                      />
                    ),
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" pb={2.5} mt={-5} textAlign="center">
                      {isLastStep ? dateRange : startDateFormatted}
                    </Typography>
                    <Badge color={badgeColor}>{contract.contractType}</Badge>
                    <EmployeeOfficeField sx={{ '.MuiTypography-root': { fontSize: 10 } }} value={contract.officeId} />
                  </div>
                </StepLabel>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default ContractsTimeline;
